import React, { useEffect, useState, useRef } from "react";
import { Button, message, Popconfirm, Space, Table, Tag } from "antd";
import { getTablePagination } from "../../../../utils/tablePagination";
import { DeleteOutlined } from "@ant-design/icons";
import EditIcon from "../../../ui/EditIcon";
import {
  getOpServicesListApi,
  deleteOpServicesApi,
} from "../../../../services/opservices.services";
import { formatToINR, capitalizeFirstLetter } from "../../../../helpers/utility";
import dayjs from "dayjs";
import PractitionerModal from "./PractitionerModal";
import { useSearchParams, useNavigate } from "react-router-dom";
import DeleteServiceModal from "./DeleteServiceModal";
import CommonPopOver from "../../../PatientsIP/ReceptionistIp/CommonPopOver";
import { UiDataTable } from "../../../ui";
import useQuery from "../../../../hooks/useQuery";
import {
  parsePageParam,
  updateUrlSearchParams,
} from "../../../../helpers/urlFilters";
import useIsMobile from "../../../../hooks/useIsMobile";
import usePageFillTableScroll from "../../../../hooks/usePageFillTableScroll";

const AssignedOpServicesTable = ({ searchQuery = "", onCountChange }) => {
  const navigate = useNavigate();
  const queryParams = useQuery();
  const [searchParams] = useSearchParams();
  const admissionId = searchParams.get("admission");
  const isMobile = useIsMobile();
  const tableWrapRef = useRef(null);
  const tableScroll = usePageFillTableScroll(tableWrapRef, isMobile, "services");

  const [services, setServices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const page = parsePageParam(queryParams, 1);
  const [pageSize, setPageSize] = useState(10);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const refreshServices = () => setRefresh((prev) => !prev);

  useEffect(() => {
    if (!admissionId) return;

    const params = {
      admission_id: admissionId,
      page,
      limit: pageSize,
    };
    if (searchQuery) params.search = searchQuery;

    setLoading(true);
    getOpServicesListApi(admissionId, params)
      .then((res) => {
        const data = res?.data;
        if (data?.results) {
          setServices(data.results);
          setTotalCount(data.count || 0);
        } else {
          setServices([]);
          setTotalCount(0);
        }
      })
      .catch((err) => {
        console.error(err);
        setServices([]);
        message.error("Failed to load assigned services");
      })
      .finally(() => setLoading(false));
  }, [refresh, searchQuery, admissionId, page, pageSize]);

  useEffect(() => {
    onCountChange?.(totalCount);
  }, [totalCount, onCountChange]);

  const onDelete = (ids) => {
    deleteOpServicesApi({ service_ids: Array.isArray(ids) ? ids : [ids] })
      .then((res) => {
        message.success("Services deleted successfully");
        refreshServices();
        setSelectedRowKeys([]);
      })
      .catch((err) =>
        message.error(err?.response?.data?.message || "Delete failed")
      );
  };

  const columns = [
    {
      title: "Service Name",
      dataIndex: "name",
      key: "name",
      width: 220,
      ellipsis: true,
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      width: 120,
      render: (v) => formatToINR(v),
    },
    {
      title: "Date",
      dataIndex: "from_date",
      key: "from_date",
      width: 140,
      render: (v) => (v ? dayjs(v).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 160,
      render: (value, record) => {
        return (
          <Space>
            <Tag
              color={
                value === "Delete_Requested"
                  ? "orange"
                  : value === "Ordered"
                  ? "blue"
                  : value === "Cancelled"
                  ? "red"
                  : "green"
              }
            >
              {value === "Delete_Requested"
                ? "Delete Requested"
                : capitalizeFirstLetter(value)}
            </Tag>
            {record?.service_delete_request_reason && (
              <CommonPopOver record={record} />
            )}
          </Space>
        );
      },
    },

    {
      title: "Updated By",
      dataIndex: "ordered_by_name",
      key: "ordered_by_name",
      ellipsis: true,
      render: (v) => v || "-",
    },
    {
      title: "Performed By",
      dataIndex: "performed_by_name",
      key: "performed_by_name",
      ellipsis: true,
      render: (v) => (v ? capitalizeFirstLetter(v) : "-"),
    },
    {
      title: "Action",
      width: 100,
      align: "center",
      render: (_, record) => {
        return record?.status === "Done" && !record?.service_delete_rejected ? (
          <Popconfirm
            title="Sure to raise delete request?"
            onConfirm={() => {
              setEditData(record?.id);
              setOpenDeleteModal(true);
            }}
          >
            <Button
              type="text"
              danger
              className="services-row-action-btn"
              aria-label="Delete"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        ) : (
          ""
        );
      },
    },
  ];

  const bulkActions =
    selectedRowKeys?.length > 0 ? (
      <Space size={4} className="services-bulk-actions">
        <Popconfirm
          title="Are you sure you want to delete selected services?"
          onConfirm={() => onDelete(selectedRowKeys)}
        >
          <Button
            danger
            type="text"
            className="services-bulk-action-btn"
            aria-label="Delete selected"
            icon={<DeleteOutlined />}
          />
        </Popconfirm>
        <Button
          type="text"
          className="services-bulk-action-btn"
          aria-label="Edit"
          icon={<EditIcon size={16} />}
          onClick={() => setOpen(true)}
        />
      </Space>
    ) : null;

  return (
    <>
      {bulkActions ? (
        <div className="services-bulk-actions-bar">
          {bulkActions}
        </div>
      ) : null}
      <UiDataTable showSearch={false} showHeader={false} shellClassName="ui-table-fill-shell">
        <div ref={tableWrapRef}>
          <Table
            className="no-vertical-borders"
            tableLayout="fixed"
            rowKey="id"
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
              getCheckboxProps: (record) => ({
                disabled:
                  record.status === "Done" ||
                  record.status === "Delete_Requested",
              }),
            }}
            dataSource={services}
            columns={columns}
            pagination={getTablePagination({
              current: page,
              pageSize,
              total: totalCount ?? 0,
              onChange: (p) => updateUrlSearchParams(navigate, { page: p }),
            })}
            loading={loading}
            scroll={isMobile ? { x: "max-content" } : tableScroll}
          />
        </div>
      </UiDataTable>
      {open && (
        <PractitionerModal
          open={open}
          setOpen={setOpen}
          refresh={refreshServices}
          selectedRowKeys={selectedRowKeys}
          setSelectedRowKeys={setSelectedRowKeys}
        />
      )}
      {openDeleteModal && (
        <DeleteServiceModal
          open={openDeleteModal}
          setOpen={setOpenDeleteModal}
          editData={editData}
          refresh={refreshServices}
          selectedRowKeys={selectedRowKeys}
          setSelectedRowKeys={setSelectedRowKeys}
        />
      )}
    </>
  );
};

export default AssignedOpServicesTable;
